import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge, formatDate, DeadlineWarning, projectInfo, deadlineLabel, deadlineExpired, trFold, SearchClear } from "@/components/shared";
import { ArrowRight, Search, Trophy, XCircle, Clock, Inbox, LayoutList } from "lucide-react";

const classify = (r) => {
  if (r.my_offer?.status === "accepted") return "won";
  if (r.my_offer?.status === "rejected") return "lost";
  if (deadlineExpired(r.deadline, r.deadline_time)) return "expired";
  return "active";
};

const TABS = [
  { key: "all", label: "Tümü", icon: LayoutList },
  { key: "active", label: "Aktif", icon: Inbox },
  { key: "expired", label: "Sona Erenler", icon: Clock },
  { key: "won", label: "Kazanılanlar", icon: Trophy },
  { key: "lost", label: "Kaybedilenler", icon: XCircle },
];

const STATUS_FILTERS = [
  { key: "", label: "Tüm Durumlar" },
  { key: "beklemede", label: "Beklemede" },
  { key: "teklif_verildi", label: "Teklif verildi" },
  { key: "suresi_doldu", label: "Süresi doldu" },
  { key: "won", label: "Kazanıldı" },
  { key: "lost", label: "Kaybedildi" },
];

export default function SupplierRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    api.get("/requests").then((r) => setRequests(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const counts = useMemo(() => {
    const c = { all: requests.length, active: 0, expired: 0, won: 0, lost: 0 };
    requests.forEach((r) => { c[classify(r)] += 1; });
    return c;
  }, [requests]);

  const visible = useMemo(() => {
    let list = requests;
    if (tab !== "all") list = list.filter((r) => classify(r) === tab);
    if (tab === "all" && statusFilter) {
      list = list.filter((r) => {
        if (statusFilter === "won") return classify(r) === "won";
        if (statusFilter === "lost") return classify(r) === "lost";
        return r.status === statusFilter;
      });
    }
    const q = trFold(search.trim());
    if (q) {
      list = list.filter((r) => {
        const hay = trFold(`${r.title || ""} ${r.project ? projectInfo(r.project) : ""}`);
        return hay.includes(q);
      });
    }
    return list;
  }, [requests, tab, search, statusFilter]);

  const emptyText = loaded
    ? (search || statusFilter ? "Aramanızla eşleşen talep bulunamadı." : "Bu sekmede talep yok.")
    : "Yükleniyor...";

  return (
    <div className="space-y-6 fade-in">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Tedarikçi Paneli</div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1">Teklif Talepleri</h1>
      </div>

      <div className="flex border-b border-slate-200 overflow-x-auto" data-testid="supplier-request-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`supplier-tab-${t.key}`}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors duration-200 ${
              tab === t.key ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-700"
            }`}
          >
            <t.icon size={14} /> {t.label} ({counts[t.key]})
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="İhale veya proje ara..."
            data-testid="supplier-request-search"
            className="rounded-none pl-9 pr-9 h-10 bg-white border-slate-200"
          />
          <SearchClear value={search} onClear={() => setSearch("")} testid="supplier-search-clear" />
        </div>
        {tab === "all" && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="supplier-status-filter"
            className="h-10 px-3 text-sm border border-slate-200 bg-white text-slate-700 focus:outline-none focus:border-slate-400"
          >
            {STATUS_FILTERS.map((f) => (
              <option key={f.key} value={f.key}>{f.label}</option>
            ))}
          </select>
        )}
      </div>

      <div className="md:hidden space-y-3" data-testid="supplier-requests-cards">
        {visible.length === 0 ? (
          <p className="border border-slate-200 bg-white p-6 text-sm text-slate-500 text-center">{emptyText}</p>
        ) : (
          visible.map((r) => (
            <div key={r.id} className="border border-slate-200 bg-white p-4 space-y-3" data-testid={`supplier-request-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                    {r.title}
                    {r.unread_messages > 0 && (
                      <span className="bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5">{r.unread_messages} yeni mesaj</span>
                    )}
                  </div>
                  {r.project && <div className="text-xs text-slate-500 mt-1">{projectInfo(r.project)}</div>}
                </div>
                <StatusBadge status={r.status} />
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Son tarih: <span className="font-medium text-slate-900">{deadlineLabel(r)}</span></span>
                <DeadlineWarning deadline={r.deadline} time={r.deadline_time} />
              </div>
              {r.my_offer && r.my_offer.status !== "pending" && <StatusBadge status={r.my_offer.status} />}
              <Link to={`/tedarikci/talepler/${r.id}`} className="block" data-testid={`supplier-request-card-link-${r.id}`}>
                <Button className="w-full rounded-none h-11 bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold">
                  {r.status === "beklemede" ? "Teklif Ver" : "Görüntüle"} <ArrowRight size={14} />
                </Button>
              </Link>
            </div>
          ))
        )}
      </div>

      <div className="hidden md:block border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3 font-medium">Teklif Adı</th>
              <th className="px-4 py-3 font-medium">Son Tarih</th>
              <th className="px-4 py-3 font-medium">Durum</th>
              <th className="px-4 py-3 font-medium text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center text-slate-500" data-testid="supplier-requests-empty">{emptyText}</td>
              </tr>
            ) : (
              visible.map((r) => (
                <tr key={r.id} className="border-b border-slate-200 last:border-b-0 hover:bg-white transition-colors duration-200" data-testid={`supplier-request-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium flex items-center gap-2 flex-wrap">
                      {r.title}
                      {r.unread_messages > 0 && (
                        <span className="bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5" data-testid={`supplier-unread-${r.id}`}>{r.unread_messages} yeni mesaj</span>
                      )}
                    </div>
                    {r.project && <div className="text-xs text-slate-500 mt-0.5">{projectInfo(r.project)}</div>}
                    <div className="text-xs text-slate-500 mt-0.5">Gönderilme: {formatDate(r.created_at)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div>{deadlineLabel(r)}</div>
                    <DeadlineWarning deadline={r.deadline} time={r.deadline_time} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={r.status} />
                    {r.my_offer && r.my_offer.status !== "pending" && (
                      <div className="mt-1.5"><StatusBadge status={r.my_offer.status} /></div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/tedarikci/talepler/${r.id}`} data-testid={`supplier-request-link-${r.id}`}>
                      <Button variant="outline" size="sm" className="rounded-none border-slate-200 hover:bg-slate-100 gap-2 h-8">
                        {r.status === "beklemede" ? "Teklif Ver" : "Görüntüle"} <ArrowRight size={13} />
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
